<template>
  <div class="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
    <TopNav @toggle-sidebar="toggleSidebar" />
    <SideMenu :isOpen="isSidebarOpen" @close-sidebar="isSidebarOpen = false" />

    <main class="p-6 md:ml-64 h-auto pt-20 flex justify-center">
      <section class="w-full max-w-lg dark:bg-gray-800 rounded-lg p-8">
        <div class="mb-6 flex justify-between items-center">
          <h2 class="text-3xl font-semibold text-gray-800 dark:text-gray-100">Create A New Branch</h2>
          <a :href="$url('/branches')"
            class="mt-4 ml-4 flex items-center text-primary-700 dark:text-primary-300 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span class="ml-2">Back</span>
          </a>
        </div>

        <form @submit.prevent="createBranch" enctype="multipart/form-data">
          <!-- Error Messages -->
          <div v-if="errors.length" class="mb-4 bg-red-100 text-red-800 p-3 rounded">
            <ul>
              <li v-for="(error, index) in errors" :key="index" class="text-sm">{{ error }}</li>
            </ul>
          </div>

          <!-- Branch Name -->
          <div class="mb-4">
            <label for="name" class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Branch
              Name</label>
            <input type="text" id="name" v-model="branch.name"
              class="w-full p-3 border rounded-lg text-gray-800 dark:text-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500"
              required />
          </div>

          <!-- URL -->
          <div class="mb-4">
            <label for="url" class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">
              Branch URL
            </label>

            <div
              class="flex items-center space-x-1 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2 text-sm font-mono text-gray-700 dark:text-gray-300">
              <span class="select-none">https://</span>
              <span class="truncate max-w-[30%]" :title="instanceDomain">{{ instanceDomain }}</span>
              <span class="select-none">/branch/</span>
              <input id="url" v-model="branch.url" @input="validateSlug" placeholder="branch-slug"
                class="flex-grow bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autocomplete="off" spellcheck="false" required
                :class="slugError ? 'ring-2 ring-red-500 border-red-500' : ''" style="min-width: 90px;" />
            </div>

            <p v-if="slugError" class="mt-1 text-xs text-red-500">{{ slugError }}</p>
          </div>


          <!-- Logo File Upload -->
          <div class="mb-4">
            <label for="logoFile" class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Logo</label>
            <input type="file" id="logoFile" @change="handleLogoUpload"
              class="w-full p-3 border rounded-lg text-gray-800 dark:text-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500"
              accept="image/*" />
          </div>

          <!-- Submit Button -->
          <div class="mt-6">
            <button type="submit" :disabled="isSubmitting"
              class="w-full !bg-secondary-600 text-white font-semibold py-3 rounded-lg hover:!bg-secondary-700 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 flex items-center justify-center">
              <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              {{ isSubmitting ? 'Creating...' : 'Create Branch' }}
            </button>
          </div>
        </form>
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import TopNav from '@/components/headers/TopNav.vue';
import SideMenu from '@/components/SideMenu.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    TopNav,
    SideMenu,
  },
  data() {
    return {
      branch: {
        name: '',
        url: '',
        theme: '',
        language: 'en',
        is_default: false,
      },
      slugError: null,
      logoFile: null, // File object for logo
      isSubmitting: false,
      errors: [],
      isSidebarOpen: false,
    };
  },
  computed: {
    ...mapGetters(['getInstance']), // assuming you have this getter

    instanceDomain() {
      const instance = this.getInstance;
      if (instance && instance.domain) {
        return `${instance.domain}.${process.env.VUE_APP_BASE_DOMAIN || 'jamlms.co.uk'}`;
      }
      // fallback placeholder
      return `your-instance.${process.env.VUE_APP_BASE_DOMAIN || 'jamlms.co.uk'}`;
    },
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    handleLogoUpload(event) {
      this.logoFile = event.target.files[0];
    },
    validateSlug() {
      // slug validation regex: only lowercase letters, numbers, hyphens
      const slugPattern = /^[a-z0-9-]+$/;
      if (!this.branch.url) {
        this.slugError = 'Branch url is required.';
      } else if (!slugPattern.test(this.branch.url)) {
        this.slugError = 'Branch url can only contain lowercase letters, numbers, and hyphens.';
      } else {
        this.slugError = null;
      }
    },
    async createBranch() {
      this.isSubmitting = true;
      this.errors = [];
      const branchUrl = this.$store.state.branch ? this.$store.state.branch.url : null;

      const formData = new FormData();
      formData.append("operations", JSON.stringify({
        query: `
      mutation CreateBranch($input: BranchInput!, $logo: Upload, $branchUrl: String) {
        createBranch(input: $input, logo: $logo, branchUrl: $branchUrl) {
          id
          name
          logo_url
        }
      }
    `,
        variables: {
          input: {
            name: this.branch.name,
            url: this.branch.url,
            theme: this.branch.theme,
            language: this.branch.language,
            is_default: this.branch.is_default,
          },
          logo: null,
          branchUrl,
        },
      }));

      formData.append("map", JSON.stringify({ "1": ["variables.logo"] }));
      formData.append("1", this.logoFile);

      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.errors && response.data.errors.length) {
          // GraphQL errors present, show them in UI
          this.errors = response.data.errors.map(err => err.message);
        } else if (response.data.data && response.data.data.createBranch) {
          // Success, navigate
          this.$routerPushWithBranch('ViewBranch', { params: { id: response.data.data.createBranch.id } });
        } else {
          this.errors.push('Unexpected response from server.');
        }
      } catch (error) {
        // Network or unexpected error
        console.error('GraphQL network error:', error);
        this.errors = [];

        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors.map(err => err.message);
        } else if (error.message) {
          this.errors.push(error.message);
        } else {
          this.errors.push('An unknown error occurred.');
        }
      } finally {
        this.isSubmitting = false;
      }

    }



  },
};
</script>
